import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Input, Spinner } from "reactstrap";
import useSecureMediaUrl from "hooks/useSecureMediaUrl";
import { formatDateTimeForDisplay } from "utils/dateUtils";
import { fileLabel, fileTone, formatBytes, isImageName } from "utils/emailAttachments";

const FileMark = ({ name, size = 40 }) => {
  const tone = fileTone(name);
  return (
    <div
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `${tone}22`,
        color: tone,
        fontSize: size > 36 ? 11 : 10,
        fontWeight: 700,
        letterSpacing: "0.04em",
      }}
    >
      {fileLabel(name)}
    </div>
  );
};

const SecureThumb = ({ url, name }) => {
  const { blobUrl, isLoading } = useSecureMediaUrl(url, { enabled: !!url });
  if (isLoading || !blobUrl) return <FileMark name={name} />;
  return (
    <img
      src={blobUrl}
      alt={name || "Preview"}
      style={{ width: 40, height: 40, objectFit: "cover", flex: "0 0 40px", background: "#111" }}
    />
  );
};

const DocumentRow = ({ item, source, selected, accent, isLightTheme, text, muted, border, onToggle }) => {
  const name = item?.media?.name || "Untitled";
  const description = item?.description || "";
  const when = formatDateTimeForDisplay(item?.created_date || item?.createdAt);
  const who = item?.created_user?.full_name || "";
  const size = formatBytes(item?.media?.size);
  const meta = [size, when !== "-" ? when : "", who].filter(Boolean).join(" · ");
  const showImage = isImageName(name, item?.media?.mime);

  return (
    <button
      type="button"
      onClick={() => onToggle(item, source)}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        textAlign: "left",
        padding: "8px 10px",
        marginBottom: 6,
        cursor: "pointer",
        background: selected
          ? isLightTheme
            ? "rgba(0,0,0,0.04)"
            : "rgba(255,255,255,0.06)"
          : "transparent",
        color: text,
        border: `1px solid ${selected ? accent : border}`,
        borderLeft: `3px solid ${selected ? accent : border}`,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 16,
          height: 16,
          flex: "0 0 16px",
          border: `1px solid ${selected ? accent : border}`,
          background: selected ? accent : "transparent",
          color: "#fff",
          fontSize: 11,
          lineHeight: "14px",
          textAlign: "center",
          fontWeight: 700,
        }}
      >
        {selected ? "✓" : ""}
      </span>
      {showImage ? <SecureThumb url={item?.media?.url} name={name} /> : <FileMark name={name} />}
      <span style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name}
        </span>
        {description ? (
          <span
            style={{
              display: "block",
              fontSize: 11,
              color: muted,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {description}
          </span>
        ) : null}
        {meta ? (
          <span style={{ display: "block", fontSize: 11, color: muted }}>{meta}</span>
        ) : null}
      </span>
    </button>
  );
};

const EmailDocumentPicker = ({
  isOpen,
  onClose,
  onConfirm,
  isLightTheme = true,
  accent = "#1d8cf8",
  files = [],
  ecabinets = [],
  isLoading = false,
  selectedIds = [],
  recordLabel = "account",
}) => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set(selectedIds));
  const selectionKey = selectedIds.join(",");
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    const initialIds = selectionKey
      ? selectionKey
          .split(",")
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id))
      : [];
    setQuery("");
    setSelected(new Set(initialIds));
    const onKey = (event) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, selectionKey]);

  const needle = query.trim().toLowerCase();
  const matchItem = (item) => {
    if (!needle) return true;
    const name = String(item?.media?.name || "").toLowerCase();
    const description = String(item?.description || "").toLowerCase();
    return name.includes(needle) || description.includes(needle);
  };

  const visibleFiles = files.filter(matchItem);
  const visibleEcabinets = ecabinets.filter(matchItem);

  if (!isOpen) return null;

  const panelBg = isLightTheme ? "#ffffff" : "#1e1e2f";
  const headerBg = isLightTheme ? "#f4f6fb" : "#27293d";
  const text = isLightTheme ? "#1e1e2f" : "#ffffff";
  const muted = isLightTheme ? "#6c757d" : "rgba(255,255,255,0.62)";
  const border = isLightTheme ? "#d9dee7" : "rgba(255,255,255,0.14)";

  const toggle = (item) => {
    const id = Number(item?.id);
    if (!Number.isFinite(id)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirm = () => {
    const chosen = [];
    files.forEach((item) => {
      if (selected.has(Number(item?.id))) chosen.push({ ...item, source: "files" });
    });
    ecabinets.forEach((item) => {
      if (selected.has(Number(item?.id))) chosen.push({ ...item, source: "ecabinet" });
    });
    onConfirm(chosen);
  };

  const renderGroup = (title, items, source, emptyLabel) => (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            color: muted,
          }}
        >
          {title}
        </span>
        <span style={{ fontSize: 11, color: muted }}>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div
          style={{
            padding: "14px 12px",
            border: `1px dashed ${border}`,
            color: muted,
            fontSize: 12,
          }}
        >
          {emptyLabel}
        </div>
      ) : (
        items.map((item) => (
          <DocumentRow
            key={`${source}-${item.id}`}
            item={item}
            source={source}
            selected={selected.has(Number(item.id))}
            accent={accent}
            isLightTheme={isLightTheme}
            text={text}
            muted={muted}
            border={border}
            onToggle={toggle}
          />
        ))
      )}
    </div>
  );

  return ReactDOM.createPortal(
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 4100,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Attach documents"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(720px, 100%)",
          maxHeight: "min(82vh, 760px)",
          display: "flex",
          flexDirection: "column",
          background: panelBg,
          color: text,
          border: `1px solid ${border}`,
          boxShadow: "0 16px 48px rgba(0,0,0,0.35)",
        }}
      >
        <div
          style={{
            padding: "14px 16px",
            background: headerBg,
            borderBottom: `1px solid ${border}`,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.04em" }}>
              ATTACH DOCUMENT
            </div>
            <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>
              Select files or eCabinet documents saved on this {recordLabel}.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: `1px solid ${border}`,
              background: "transparent",
              color: text,
              width: 28,
              height: 28,
              fontSize: 16,
              lineHeight: "26px",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: "12px 16px 0" }}>
          <Input
            type="text"
            bsSize="sm"
            value={query}
            placeholder="Search by name or description"
            onChange={(event) => setQuery(event.target.value)}
            style={{
              height: 36,
              fontSize: 12,
              background: isLightTheme ? "#fff" : "transparent",
              color: text,
              borderColor: border,
            }}
          />
        </div>

        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
          {isLoading ? (
            <div style={{ textAlign: "center", padding: 28, color: muted }}>
              <Spinner size="sm" className="mr-2" />
              Loading documents...
            </div>
          ) : (
            <>
              {renderGroup(
                "FILES",
                visibleFiles,
                "files",
                needle ? "No matching files." : `No files on this ${recordLabel}.`
              )}
              {renderGroup(
                "ECABINET",
                visibleEcabinets,
                "ecabinet",
                needle ? "No matching eCabinet documents." : `No eCabinet documents on this ${recordLabel}.`
              )}
            </>
          )}
        </div>

        <div
          style={{
            padding: "12px 16px",
            borderTop: `1px solid ${border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            background: headerBg,
          }}
        >
          <span style={{ fontSize: 12, color: muted }}>{selected.size} selected</span>
          <div>
            <button
              type="button"
              onClick={onClose}
              style={{
                marginRight: 8,
                padding: "6px 16px",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.04em",
                background: "transparent",
                color: text,
                border: `1px solid ${border}`,
                cursor: "pointer",
              }}
            >
              CANCEL
            </button>
            <button
              type="button"
              onClick={confirm}
              style={{
                padding: "6px 16px",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.04em",
                background: accent,
                color: "#fff",
                border: `1px solid ${accent}`,
                cursor: "pointer",
              }}
            >
              ATTACH
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EmailDocumentPicker;
